import React from 'react';
import PropTypes from 'prop-types';
import moment from 'moment';
import classnames from 'classnames';
import {groupBy} from 'lodash';
import {i18n, get3charMonth} from '../../Utility/Format';

export const granularities = [
    {
        identifier: '1d',
        label: i18n('dbm.currencyPair.granularities.1d'),
        data: '1T',
        startTime: moment().startOf('day'),
        endTime: moment().endOf('day'),
        maxTick: 1440,
        ticks: 12,
        tickFormatter: (moment) => {
            const timeFormat = moment.locale() === 'en' ? 'h:00 a' : 'H:00';
            return v => moment(v).format(timeFormat);
        },
        formatData: (chartData) => {
            const data = chartData.slice();  // as this might come through props, clone it to avoid messing with 'immutables'
            if (data.length > 0) {
                // fill timestamps  until 23:59 pm
                const start = moment(data[data.length - 1].timestamp).add(1, 'minutes');
                const end = moment().endOf('day');
                for (let m = start; m.diff(end, 'minutes') < 0; m.add(1, 'minutes')) {
                    data.push({ timestamp: m.format('YYYY-MM-DDTHH:mm:00') });
                }
            }
            return data;
        }
    },
    {
        identifier: '1m',
        label: i18n('dbm.currencyPair.granularities.1m'),
        data: 'MAX',
        startTime: moment().subtract(30, 'days'),
        endTime: moment(),
        maxTick: 30,
        ticks: 15,
        tickFormatter: (moment) => {
            return v => moment(v).format(`DD. [${get3charMonth(moment, v)}]`);
        }
    },
    {
        identifier: '3m',
        label: i18n('dbm.currencyPair.granularities.3m'),
        data: 'MAX',
        startTime: moment().subtract(90, 'days'),
        endTime: moment(),
        maxTick: 90,
        ticks: 15,
        tickFormatter: (moment) => {
            return v => moment(v).format(`DD. [${get3charMonth(moment, v)}]`);
        }
    },
    {
        identifier: '6m',
        label: i18n('dbm.currencyPair.granularities.6m'),
        data: 'MAX',
        startTime: moment().subtract(180, 'days'),
        endTime: moment(),
        tickFormatter: (moment) => {
            return v => moment(v).format(`DD. [${get3charMonth(moment, v)}]`);
        },
        getTicksByData: (data) => {
            let ticks = [];
            let dataByMonths = groupBy(data, item => moment(item.timestamp).month());
            Object.keys(dataByMonths).forEach(month => {
                const monthData = dataByMonths[month];
                const mid = Math.floor(monthData.length / 2);
                ticks.push(monthData[0]['timestamp']);
                if (mid >= 6) {
                    ticks.push(monthData[mid]['timestamp']);
                }
            });
            return ticks;
        }
    },
    {
        identifier: 'ytd',
        label: i18n('dbm.currencyPair.granularities.ytd'),
        data: 'MAX',
        startTime: moment().startOf('year'),
        endTime: moment(),
        ticks: 12,
        tickFormatter: (moment, values) => {
            if (values <= 180) {
                return v => moment(v).format(`DD. [${get3charMonth(moment, v)}]`);
            }
            return v => get3charMonth(moment, v);
        }
    },
    {
        identifier: '1y',
        label: i18n('dbm.currencyPair.granularities.1y'),
        data: 'MAX',
        startTime: moment().subtract(1, 'years'),
        endTime: moment(),
        tickFormatter: (moment) => {
            return v => get3charMonth(moment, v);
        },
        getTicksByData: (data) => {
            let ticks = [];
            let dataByMonths = groupBy(data, item => moment(item.timestamp).month());
            Object.keys(dataByMonths).forEach(month => {
                ticks.push(dataByMonths[month][0]['timestamp']);
            });
            return ticks;
        }
    },
    {
        identifier: '5y',
        label: i18n('dbm.currencyPair.granularities.5y'),
        data: 'MAX',
        startTime: moment().subtract(5, 'years'),
        endTime: moment(),
        maxTick: 1825,
        ticks: 12,
        tickFormatter: (moment) => {
            return v => moment(v).format(`[${get3charMonth(moment, v)}] YY`);
        }
    },
    {
        identifier: '10y',
        label: i18n('dbm.currencyPair.granularities.10y'),
        data: 'MAX',
        startTime: moment().subtract(10, 'years'),
        endTime: moment(),
        maxTick: 3650,
        ticks: 12,
        tickFormatter: (moment) => {
            return v => moment(v).format(`[${get3charMonth(moment, v)}] YY`);
        }
    },
    {
        identifier: 'max',
        label: i18n('dbm.currencyPair.granularities.max'),
        data: 'MAX',
        startTime: moment().subtract(1000, 'years'), // needs just to be way back
        endTime: moment(),
        ticks: 12,
        tickFormatter: (moment) => {
            return v => moment(v).format(`[${get3charMonth(moment, v)}] YY`);
        }
    }
];

const CurrencyPairGranularitySelector = (props) => {

    const currentGranularity = props.currentGranularity;

    return (
        <div className="granularity-selector">
            {
                granularities.map((granularity, index) => {
                    const duration = granularity.endTime.diff(granularity.startTime, 'days');
                    const isSelectable = (duration <= props.durationOfChartData) || (granularity.identifier === 'max' && props.durationOfChartData > 0);
                    const classNames = classnames('single-granularity',
                        currentGranularity.identifier === granularity.identifier ? 'is-active' : null,
                        isSelectable ? null : 'is-not-available'
                    );

                    return (
                        <div className={classNames} key={'granularity-' + index} onClick={() => {
                            if (isSelectable) {
                                props.onSelectGranularity(granularity)
                            }
                        }}>
                            <span>{granularity.label}</span>
                        </div>
                    )
                })
            }
        </div>
    );
};

CurrencyPairGranularitySelector.propTypes = {
    currentGranularity: PropTypes.shape({
        identifier: PropTypes.string.isRequired
    }),
    onSelectGranularity: PropTypes.func.isRequired,
    durationOfChartData: PropTypes.number.isRequired
};

export default CurrencyPairGranularitySelector;